import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../context/AuthContext';
import { sessionService } from '../services/sessionService';
import type { CleaningSession, SessionPhoto } from '../services/sessionService';
import { getRankInfo } from '../utils/rankUtils';
import Modal from './Modal';
import { supabase } from '../lib/supabase';
import './BeforeAfterCamera.css';

interface LocalPhoto {
  data: string;
  timestamp: string;
  dbPhoto?: SessionPhoto;
}

interface BeforeAfterCameraProps {
  onNavigate: (page: string) => void;
  onPointsEarned: (points: number, activityType?: 'before-after' | 'video', location?: string) => void;
}

const BeforeAfterCamera: React.FC<BeforeAfterCameraProps> = ({ onNavigate, onPointsEarned }) => {
  const { user } = useAuth();
  const [mode, setMode] = useState<'before' | 'after'>('before');
  const [photos, setPhotos] = useState<{ before: LocalPhoto[], after: LocalPhoto[] }>({ before: [], after: [] });
  const [currentSession, setCurrentSession] = useState<CleaningSession | null>(null);
  const [showCamera, setShowCamera] = useState(true);
  const [showPreview, setShowPreview] = useState(false);
  const [showComparison, setShowComparison] = useState(false);
  const [showPostModal, setShowPostModal] = useState(false);
  const [previewImage, setPreviewImage] = useState<string>('');
  const [caption, setCaption] = useState('');
  const [location, setLocation] = useState('');
  const [stream, setStream] = useState<MediaStream | null>(null);
  const [hasCamera, setHasCamera] = useState(true);
  const [cameraError, setCameraError] = useState(false);
  const [isVideoReady, setIsVideoReady] = useState(false);
  const [cameraErrorType, setCameraErrorType] = useState<'permission' | 'not-found' | 'unknown' | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [showImageModal, setShowImageModal] = useState(false);
  const [modalImage, setModalImage] = useState('');
  const [modalState, setModalState] = useState<{
    show: boolean;
    type: 'success' | 'error' | 'info';
    title?: string;
    message: string;
  }>({ show: false, type: 'info', message: '' });
  
  const videoRef = useRef<HTMLVideoElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const multiFileInputRef = useRef<HTMLInputElement>(null);
  const maxPhotos = 5;

  useEffect(() => {
    initializeSession();
    checkCameraAvailability();
    return () => {
      stopCamera();
    };
  }, []);

  useEffect(() => {
    if (hasCamera && !cameraError) {
      startCamera();
    }
  }, [hasCamera]);

  // Handle visibility change (tab switching)
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.hidden && stream) {
        stopCamera();
      } else if (!document.hidden && hasCamera && !stream) {
        startCamera();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [stream, hasCamera]);

  const initializeSession = async () => {
    let session = await sessionService.getActiveSession();
    if (!session) {
      session = await sessionService.createSession('Campus Cleaning');
    }
    setCurrentSession(session);
    
    // Load existing photos if any
    const existingPhotos = await sessionService.getSessionPhotos(session.id);
    const beforePhotos = existingPhotos.filter(p => p.type === 'before');
    const afterPhotos = existingPhotos.filter(p => p.type === 'after');
    
    if (beforePhotos.length > 0 || afterPhotos.length > 0) {
      setPhotos({
        before: beforePhotos.map(p => ({ 
          data: p.storage_url, 
          timestamp: p.created_at || new Date().toISOString(),
          dbPhoto: p 
        })),
        after: afterPhotos.map(p => ({ 
          data: p.storage_url, 
          timestamp: p.created_at || new Date().toISOString(),
          dbPhoto: p 
        }))
      });
    }
  };

  const checkCameraAvailability = async () => {
    try {
      const devices = await navigator.mediaDevices.enumerateDevices();
      const videoDevices = devices.filter(device => device.kind === 'videoinput');
      setHasCamera(videoDevices.length > 0);
    } catch (err) {
      setHasCamera(false);
    }
  };

  const stopCamera = () => {
    if (stream) {
      stream.getTracks().forEach(track => {
        track.stop();
        track.enabled = false;
      });
      setStream(null);
    }
    if (videoRef.current) {
      videoRef.current.srcObject = null;
    }
    setIsVideoReady(false);
    setHasCamera(false);
  };

  const startCamera = async () => {
    try {
      stopCamera();
      setIsVideoReady(false);
      
      const stream = await navigator.mediaDevices.getUserMedia({ 
        video: { facingMode: 'environment' },
        audio: false
      });
      
      setStream(stream);
      
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        
        // Wait for video to actually load
        videoRef.current.onloadedmetadata = () => {
          videoRef.current!.play();
          setIsVideoReady(true);
        };
      }
      
      setCameraError(false);
      setHasCamera(true);
    } catch (err: any) {
      setCameraError(true);
      setIsVideoReady(false);
      
      // Set specific error type
      if (err.name === 'NotAllowedError' || err.name === 'PermissionDeniedError') {
        setCameraErrorType('permission');
      } else if (err.name === 'NotFoundError' || err.name === 'DevicesNotFoundError') {
        setCameraErrorType('not-found');
        setHasCamera(false);
      } else {
        setCameraErrorType('unknown');
      }
    }
  };

  const switchMode = (newMode: 'before' | 'after') => {
    if (newMode === 'after' && photos.before.length === 0) {
      setModalState({
        show: true,
        type: 'info',
        message: 'Please take at least one "before" photo first'
      });
      return;
    }
    
    setMode(newMode);
    setShowComparison(false);
    setShowCamera(true);
  };

  const capturePhoto = () => {
    // Check if uploading
    if (isUploading) {
      return;
    }

    // Early return if video not ready
    if (!isVideoReady) {
      setModalState({
        show: true,
        type: 'info',
        message: 'Camera is still loading, please wait a moment and try again'
      });
      return;
    }
    
    const video = videoRef.current;
    
    // Don't capture if video isn't ready
    if (!video || !video.srcObject || video.readyState < 2 || video.videoWidth === 0 || video.videoHeight === 0) {
      setModalState({
        show: true,
        type: 'info',
        message: 'Camera is still initializing, please wait a moment'
      });
      return; // Just return, don't capture blank
    }
    
    try {
      // Safe to capture now
      const canvas = document.createElement('canvas');
      canvas.width = video.videoWidth;
      canvas.height = video.videoHeight;
      const ctx = canvas.getContext('2d');
      
      if (!ctx) {
          return;
      }
      
      ctx.drawImage(video, 0, 0);
      const imageData = canvas.toDataURL('image/jpeg', 0.9);
      
      if (!imageData || imageData === 'data:,' || imageData.length < 100) {
          return;
      }
      
      setPreviewImage(imageData);
      setShowPreview(true);
      setShowCamera(false);
    } catch (error) {
      // Don't show alert or trigger upload - just fail silently
    }
  };

  const handleFileUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) {
      return;
    }
    
    if (!file.type.startsWith('image/')) {
      setModalState({
        show: true,
        type: 'info',
        message: 'Please select an image file'
      });
      return;
    }
    
    const reader = new FileReader();
    reader.onload = (e) => {
      const imageData = e.target?.result as string;
      setPreviewImage(imageData);
      setShowPreview(true);
      setShowCamera(false);
    };
    reader.onerror = () => {
      setModalState({
        show: true,
        type: 'error',
        message: 'Failed to read file'
      });
    };
    reader.readAsDataURL(file);
    
    event.target.value = '';
  };

  const handleMultiFileUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(event.target.files || []);
    
    // Process files one by one through preview
    const imageFiles = files.filter(file => file.type.startsWith('image/'));
    if (imageFiles.length === 0) {
      setModalState({
        show: true,
        type: 'info',
        message: 'Please select image files only'
      });
      return;
    }

    if (isUploading) {
      return; // Prevent multiple uploads
    }
    
    // For now, just handle the first file through preview
    // You can extend this to handle multiple files in sequence
    if (imageFiles[0]) {
      const reader = new FileReader();
      reader.onload = (e) => {
        setPreviewImage(e.target?.result as string);
        setShowPreview(true);
        setShowCamera(false);
      };
      reader.readAsDataURL(imageFiles[0]);
    }
    
    // Reset input so same file can be selected again
    event.target.value = '';
    
    if (!currentSession) {
      setModalState({
        show: true,
        type: 'error',
        message: 'No active session. Please refresh and try again.'
      });
      return;
    }
    
    const currentCount = photos[mode].length;
    const availableSlots = maxPhotos - currentCount;
    
    if (availableSlots <= 0) {
      setModalState({
        show: true,
        type: 'info',
        message: `Maximum ${maxPhotos} ${mode} photos allowed`
      });
      return;
    }
    
    const filesToProcess = imageFiles.slice(0, availableSlots);
    if (filesToProcess.length < imageFiles.length) {
      setModalState({
        show: true,
        type: 'info',
        message: `Only processing first ${filesToProcess.length} images due to limit`
      });
    }
    
    setShowCamera(false);
    setIsUploading(true);
    setUploadError(null);
    
    try {
      // Read all files
      const photoPromises = filesToProcess.map(file => {
        return new Promise<string>((resolve, reject) => {
          const reader = new FileReader();
          reader.onload = (e) => resolve(e.target?.result as string);
          reader.onerror = reject;
          reader.readAsDataURL(file);
        });
      });
      
      const photosData = await Promise.all(photoPromises);
      
      // Upload to backend
      const photosToUpload = photosData.map(data => ({ data, type: mode as 'before' | 'after' }));
      const uploadedPhotos = await sessionService.uploadMultiplePhotos(
        currentSession.id,
        photosToUpload,
        currentCount
      );
      
      // Update local state
      const newLocalPhotos = uploadedPhotos.map((dbPhoto, index) => ({
        data: photosData[index],
        timestamp: new Date().toISOString(),
        dbPhoto
      }));
      
      setPhotos(prev => ({
        ...prev,
        [mode]: [...prev[mode], ...newLocalPhotos]
      }));
      
      // Auto-pair if after mode
      if (mode === 'after') {
        await sessionService.autoPairPhotos(currentSession.id);
      }
      
      setModalState({
        show: true,
        type: 'success',
        message: `Uploaded ${uploadedPhotos.length} ${mode} photos successfully!`
      });
      setShowCamera(true);
      
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to upload some images. Please try again.';
      setUploadError(errorMessage);
      setModalState({
        show: true,
        type: 'error',
        message: errorMessage
      });
      setShowCamera(true);
    } finally {
      setIsUploading(false);
    }
    
    event.target.value = '';
  };

  const triggerFileUpload = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    } else {
    }
  };

  const triggerMultiFileUpload = () => {
    multiFileInputRef.current?.click();
  };

  const usePhoto = async () => {
    if (!currentSession) {
      setModalState({
        show: true,
        type: 'error',
        message: 'No active session. Please refresh and try again.'
      });
      return;
    }

    if (!previewImage) {
      setModalState({
        show: true,
        type: 'error',
        message: 'No image to save'
      });
      return;
    }

    if (isUploading) {
      return; // Prevent multiple clicks
    }

    setIsUploading(true);
    setUploadError(null);

    try {
      if (mode === 'before') {
      if (photos.before.length >= maxPhotos) {
        setModalState({
          show: true,
          type: 'info',
          message: `Maximum ${maxPhotos} before photos allowed`
        });
        resetCamera();
        return;
      }
      
        const dbPhoto = await sessionService.uploadSessionPhoto(
          currentSession.id,
          previewImage,
          'before',
          photos.before.length
        );
        
        if (dbPhoto) {
          setPhotos(prev => ({
            ...prev,
            before: [...prev.before, { 
              data: previewImage, 
              timestamp: new Date().toISOString(),
              dbPhoto 
            }]
          }));
        } else {
          throw new Error('Failed to upload photo');
        }
      } else {
      if (photos.after.length >= photos.before.length) {
        setModalState({
          show: true,
          type: 'info',
          message: 'All before photos have been paired! Take more before photos first.'
        });
        resetCamera();
        return;
      }
      
        const dbPhoto = await sessionService.uploadSessionPhoto(
          currentSession.id,
          previewImage,
          'after',
          photos.after.length
        );
        
        if (dbPhoto) {
          setPhotos(prev => ({
            ...prev,
            after: [...prev.after, { 
              data: previewImage, 
              timestamp: new Date().toISOString(),
              dbPhoto 
            }]
          }));
          
          // Auto-pair photos
          const newPairs = await sessionService.autoPairPhotos(currentSession.id);
        } else {
          throw new Error('Failed to upload photo');
        }
      }
      
      resetCamera();
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to upload photo. Please try again.';
      setUploadError(errorMessage);
      setModalState({
        show: true,
        type: 'error',
        message: errorMessage
      });
    } finally {
      setIsUploading(false);
    }
  };

  const resetCamera = () => {
    setShowPreview(false);
    setShowCamera(true);
    setPreviewImage('');
  };

  const viewComparisons = async () => {
    if (!currentSession) return;
    
    const progress = await sessionService.getSessionProgress(currentSession.id);
    
    // Also log the current state
    
    if (progress.pairCount === 0) {
      setModalState({
        show: true,
        type: 'info',
        message: 'No photo pairs available yet. Make sure you have both before and after photos.'
      });
      return;
    }
    
    setShowComparison(true);
    setShowCamera(false);
    setShowPreview(false);
  };

  const finishSession = async () => {
    if (!currentSession) return;
    
    const progress = await sessionService.getSessionProgress(currentSession.id);
    if (!progress.canComplete) {
      setModalState({
        show: true,
        type: 'info',
        message: 'Please take at least one before/after pair'
      });
      return;
    }
    
    setShowPostModal(true);
  };

  const postToActivity = async () => {
    if (!currentSession) return;
    
    if (isUploading) {
      return; // Prevent multiple submissions
    }

    // Validate location is provided
    if (!location.trim()) {
      setModalState({
        show: true,
        type: 'error',
        message: 'Please enter the location where you performed the cleaning activity.'
      });
      return;
    }

    const finalCaption = caption || `Completed cleaning at ${location} - ${photos.before.length} area${photos.before.length > 1 ? 's' : ''}!`;
    
    setIsUploading(true);
    setUploadError(null);
    
    try {
      await sessionService.completeSession(currentSession.id, {
        duration_minutes: 30,
        difficulty: 'medium',
        caption: finalCaption,
        is_public: true
      });
      
      // Update the session with the user-provided location
      await supabase
        .from('cleaning_sessions')
        .update({ location: location.trim() })
        .eq('id', currentSession.id);
      
      const result = await sessionService.postToActivityFeed(
        currentSession.id,
        finalCaption,
        false,
        'medium'
      );
      
      if (result.success) {
        setModalState({
          show: true,
          type: 'success',
          title: 'Success!',
          message: 'Activity submitted successfully! Your submission is pending admin approval. Points will be awarded once approved.'
        });
        
        // Navigate after a short delay
        setTimeout(() => {
          stopCamera();
          onNavigate('dashboard');
        }, 2500);
      }
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to save activity. Please try again.';
      setUploadError(errorMessage);
      setModalState({
        show: true,
        type: 'error',
        message: errorMessage
      });
    } finally {
      setIsUploading(false);
    }
  };

  const savePrivately = async () => {
    if (!currentSession) return;
    
    if (isUploading) {
      return; // Prevent multiple submissions
    }

    // Validate location is provided
    if (!location.trim()) {
      setModalState({
        show: true,
        type: 'error',
        message: 'Please enter the location where you performed the cleaning activity.'
      });
      return;
    }

    setIsUploading(true);
    setUploadError(null);
    
    try {
      await sessionService.completeSession(currentSession.id, {
        duration_minutes: 30,
        difficulty: 'medium',
        caption: '',
        is_public: false
      });
      
      // Update the session with the user-provided location
      await supabase
        .from('cleaning_sessions')
        .update({ location: location.trim() })
        .eq('id', currentSession.id);
      
      const result = await sessionService.postToActivityFeed(
        currentSession.id,
        '',
        true,
        'medium'
      );
      
      if (result.success) {
        setModalState({
          show: true,
          type: 'success',
          title: 'Saved Privately!',
          message: 'Your submission is pending admin approval. Points will be awarded once approved.'
        });
      }
      
      onNavigate('dashboard');
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to save activity. Please try again.';
      setUploadError(errorMessage);
      setModalState({
        show: true,
        type: 'error',
        message: errorMessage
      });
    } finally {
      setIsUploading(false);
    }
  };

  const getUnpairedBeforeIndex = () => {
    // For after mode, show the next unpaired before photo
    return photos.after.length < photos.before.length ? photos.after.length : -1;
  };

  const openFullImage = (imageUrl: string) => {
    setModalImage(imageUrl);
    setShowImageModal(true);
  };

  return (
    <div className="bac-container">
      <div className="bac-header">
        <button className="bac-back-btn" onClick={() => {
          stopCamera();
          onNavigate('dashboard');
        }}>
          <svg width="24" height="24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M19 12H5M12 19l-7-7 7-7"/>
          </svg>
        </button>
        <div className="bac-mode-switcher">
          <button 
            className={`bac-mode-btn ${mode === 'before' ? 'active' : ''}`}
            onClick={() => switchMode('before')}
          >
            Before
          </button>
          <button 
            className={`bac-mode-btn ${mode === 'after' ? 'active' : ''}`}
            onClick={() => switchMode('after')}
          >
            After
          </button>
        </div>
        <div style={{ width: '40px' }}></div>
      </div>

      {showCamera && (
        <div className="bac-camera-container">
          <div className="bac-camera-header">
            <h1 className="bac-camera-title">Capture {mode === 'before' ? 'Before' : 'After'}</h1>
            <p className="bac-camera-subtitle">
              {mode === 'before' 
                ? `Take photos of areas before cleaning (${photos.before.length}/${maxPhotos})`
                : `Take matching after photos (${photos.after.length}/${photos.before.length})`
              }
            </p>
          </div>

          <div className="bac-camera-view">
            {hasCamera && !cameraError ? (
              <>
                <video 
                  ref={videoRef} 
                  autoPlay 
                  playsInline 
                  muted
                  onLoadedMetadata={() => {
                  }}
                />
                {!isVideoReady && (
                  <div className="bac-loading-overlay">
                    <div className="bac-loading-spinner"></div>
                    <p>Initializing camera...</p>
                    <p style={{ fontSize: '12px', marginTop: '8px' }}>Please wait</p>
                  </div>
                )}
                <div className="bac-camera-overlay">
                  <div className="bac-grid-overlay">
                    <div className="bac-grid-line"></div>
                    <div className="bac-grid-line"></div>
                    <div className="bac-grid-line"></div>
                    <div className="bac-grid-line"></div>
                  </div>
                </div>
              </>
            ) : (
              <div className="bac-no-camera">
                {cameraErrorType === 'permission' ? (
                  <>
                    <svg width="80" height="80" fill="none" stroke="currentColor" strokeWidth="2" opacity="0.3">
                      <circle cx="40" cy="40" r="30"/>
                      <path d="M40 25v15M40 50v0"/>
                    </svg>
                    <h3>Camera Permission Denied</h3>
                    <p>Please allow camera access in your browser settings</p>
                    <button className="bac-btn bac-btn-primary" onClick={() => window.location.reload()}>Try Again</button>
                  </>
                ) : cameraErrorType === 'not-found' ? (
                  <>
                    <svg width="80" height="80" fill="none" stroke="currentColor" strokeWidth="2" opacity="0.3">
                      <rect x="20" y="20" width="40" height="40" rx="4"/>
                      <circle cx="40" cy="40" r="12"/>
                      <path d="M20 30L10 20M20 30L30 20M50 30L60 20M50 30L40 20"/>
                    </svg>
                    <h3>No Camera Found</h3>
                    <p>Please connect a camera or use the gallery button to upload photos</p>
                  </>
                ) : (
                  <>
                    <svg width="80" height="80" fill="none" stroke="currentColor" strokeWidth="2" opacity="0.3">
                      <rect x="20" y="20" width="40" height="40" rx="4"/>
                      <circle cx="40" cy="40" r="12"/>
                    </svg>
                    <p>Camera not available</p>
                  </>
                )}
                <button className="bac-upload-btn" onClick={triggerFileUpload}>
                  <svg width="24" height="24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4M17 8l-5-5-5 5M12 3v12"/>
                  </svg>
                  Upload from Gallery
                </button>
              </div>
            )}
            {mode === 'after' && getUnpairedBeforeIndex() !== -1 && (
              <div className="bac-reference-photo">
                <img src={photos.before[getUnpairedBeforeIndex()].data} alt="Before" />
                <div className="bac-reference-label">Before</div>
              </div>
            )}
          </div>
          
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            onChange={handleFileUpload}
            style={{ display: 'none' }}
            id="single-file-input"
          />
          
          <input
            ref={multiFileInputRef}
            type="file"
            accept="image/*"
            multiple
            onChange={handleMultiFileUpload}
            style={{ display: 'none' }}
          />

          <div className="bac-camera-controls">
            <label htmlFor="single-file-input" className="bac-gallery-btn" style={{ cursor: 'pointer' }}>
              <svg width="24" height="24" fill="none" stroke="currentColor" strokeWidth="2">
                <rect x="3" y="3" width="18" height="18" rx="2"/>
                <circle cx="8.5" cy="8.5" r="1.5"/>
                <path d="M21 15l-5-5L5 21"/>
              </svg>
            </label>

            <button 
              className="bac-capture-btn" 
              onClick={capturePhoto}
              disabled={hasCamera && (!isVideoReady || cameraError)}
            >
              <div className="bac-capture-btn-inner"></div>
            </button>

            <div style={{ width: '48px' }}></div>
          </div>
        </div>
      )}

      {showPreview && (
        <div className="bac-photo-preview">
          <img className="bac-preview-image" src={previewImage} alt="Preview" />
          {isUploading && (
            <div className="bac-loading-overlay">
              <div className="bac-loading-spinner"></div>
              <p>Uploading photo...</p>
            </div>
          )}
          <div className="bac-preview-actions">
            <button className="bac-btn bac-btn-secondary" onClick={resetCamera} disabled={isUploading}>Retake</button>
            <button className="bac-btn bac-btn-primary" onClick={usePhoto} disabled={isUploading}>
              {isUploading ? 'Uploading...' : 'Use This Photo'}
            </button>
          </div>
        </div>
      )}

      {/* Thumbnail Strip */}
      {(photos.before.length > 0 || photos.after.length > 0) && !showComparison && (
        <div className="bac-thumbnail-strip">
          {photos.before.map((photo, index) => (
            <div key={`before-${index}`} className="bac-thumbnail before">
              <img 
                src={photo.data} 
                alt={`Before ${index + 1}`} 
                onClick={() => openFullImage(photo.data)}
                style={{ cursor: 'pointer' }}
              />
              <div className="bac-thumbnail-label">Before {index + 1}</div>
            </div>
          ))}
          {photos.after.map((photo, index) => (
            <div key={`after-${index}`} className="bac-thumbnail after">
              <img 
                src={photo.data} 
                alt={`After ${index + 1}`} 
                onClick={() => openFullImage(photo.data)}
                style={{ cursor: 'pointer' }}
              />
              <div className="bac-thumbnail-label">After {index + 1}</div>
            </div>
          ))}
        </div>
      )}

      {/* Action Buttons */}
      {photos.before.length > 0 && photos.after.length > 0 && !showComparison && (
        <div className="bac-action-buttons">
          <button className="bac-action-button" onClick={viewComparisons}>
            View Comparisons
          </button>
        </div>
      )}

      {/* Comparison View */}
      {showComparison && (
        <div className="bac-comparison-container">
          <div className="bac-comparison-header">
            <h2 className="bac-comparison-title">Before & After Comparison</h2>
            <div className="bac-photo-counter">
              {Math.min(photos.before.length, photos.after.length)} photo pair{Math.min(photos.before.length, photos.after.length) > 1 ? 's' : ''}
            </div>
          </div>
          
          <div className="bac-photo-pairs">
            {Array.from({ length: Math.min(photos.before.length, photos.after.length) }).map((_, index) => (
              <ComparisonSlider
                key={index}
                beforeImage={photos.before[index].data}
                afterImage={photos.after[index].data}
                pairNumber={index + 1}
                totalPairs={Math.min(photos.before.length, photos.after.length)}
              />
            ))}
          </div>
          
          <button className="bac-action-button" onClick={finishSession}>Finish & Share</button>
          <button className="bac-action-button secondary" onClick={() => {
            setShowComparison(false);
            setShowCamera(true);
          }}>
            Take More Photos
          </button>
        </div>
      )}

      {/* Post Modal */}
      {showPostModal && (
        <div className="bac-post-modal active">
          <div className="bac-modal-content">
            <h2 className="bac-modal-title">Share Your Work</h2>
            
            <div style={{ marginBottom: '15px' }}>
              <label style={{ 
                display: 'block', 
                marginBottom: '8px', 
                fontWeight: 'bold',
                color: '#333'
              }}>
                Cleaning Location <span style={{ color: '#dc3545' }}>*</span>
              </label>
              <input
                type="text"
                className="bac-location-input"
                placeholder="Enter the location you cleaned (e.g., Library, Main Building, Cafeteria)..."
                value={location}
                onChange={(e) => setLocation(e.target.value)}
                style={{
                  width: '100%',
                  padding: '12px',
                  fontSize: '14px',
                  border: '1px solid #ddd',
                  borderRadius: '8px',
                  marginBottom: '8px',
                  boxSizing: 'border-box'
                }}
                required
              />
              <small style={{ color: '#666', fontSize: '12px' }}>
                Be specific about the area cleaned (required)
              </small>
            </div>
            
            <textarea 
              className="bac-caption-input"
              placeholder="Add a caption (optional)..."
              value={caption}
              onChange={(e) => setCaption(e.target.value)}
            />
            
            <div className="bac-modal-actions">
              <button className="bac-btn bac-btn-primary" onClick={postToActivity} disabled={isUploading}>
                {isUploading ? 'Saving...' : 'Post to Activity Feed'}
              </button>
              <button className="bac-btn bac-btn-secondary" onClick={savePrivately} disabled={isUploading}>
                {isUploading ? 'Saving...' : 'Save Privately'}
              </button>
              <button 
                className="bac-btn bac-btn-secondary" 
                onClick={() => setShowPostModal(false)}
                style={{ marginTop: '10px' }}
                disabled={isUploading}
              >
                Cancel
              </button>
            </div>
            {isUploading && (
              <div style={{ marginTop: '20px', textAlign: 'center' }}>
                <div className="bac-loading-spinner" style={{ margin: '0 auto' }}></div>
                <p style={{ marginTop: '10px', color: 'var(--bac-text-secondary)' }}>Saving your progress...</p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Image Modal */}
      {showImageModal && (
        <div className="image-modal" onClick={() => setShowImageModal(false)}>
          <div className="image-modal-content" onClick={(e) => e.stopPropagation()}>
            <img src={modalImage} alt="Full size" />
            <button className="close-modal" onClick={() => setShowImageModal(false)}>×</button>
          </div>
        </div>
      )}
      
      {/* Modal for alerts */}
      <Modal
        isOpen={modalState.show}
        onClose={() => setModalState({ ...modalState, show: false })}
        type={modalState.type}
        title={modalState.title}
        message={modalState.message}
      />
    </div>
  );
};

interface ComparisonSliderProps {
  beforeImage: string;
  afterImage: string;
  pairNumber: number;
  totalPairs: number;
}

const ComparisonSlider: React.FC<ComparisonSliderProps> = ({ 
  beforeImage, 
  afterImage, 
  pairNumber, 
  totalPairs 
}) => {
  const [sliderPosition, setSliderPosition] = useState(50);
  const [isDragging, setIsDragging] = useState(false);
  const sliderRef = useRef<HTMLDivElement>(null);

  const handleMove = (clientX: number) => {
    if (!sliderRef.current) return;
    
    const rect = sliderRef.current.getBoundingClientRect();
    const position = Math.max(0, Math.min(100, ((clientX - rect.left) / rect.width) * 100));
    setSliderPosition(position);
  };

  const handleMouseDown = () => setIsDragging(true);
  const handleMouseUp = () => setIsDragging(false);
  const handleMouseMove = (e: MouseEvent) => {
    if (isDragging) {
      e.preventDefault();
      handleMove(e.clientX);
    }
  };

  const handleTouchMove = (e: TouchEvent) => {
    if (isDragging && e.touches[0]) {
      e.preventDefault();
      handleMove(e.touches[0].clientX);
    }
  };

  useEffect(() => {
    if (isDragging) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
      document.addEventListener('touchmove', handleTouchMove, { passive: false });
      document.addEventListener('touchend', handleMouseUp);
      
      return () => {
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
        document.removeEventListener('touchmove', handleTouchMove);
        document.removeEventListener('touchend', handleMouseUp);
      };
    }
  }, [isDragging]);

  return (
    <div className="bac-photo-pair">
      <div 
        className="bac-comparison-slider" 
        ref={sliderRef}
        onClick={(e) => {
          const target = e.target as HTMLElement;
          if (!target.classList.contains('bac-slider-handle')) {
            handleMove(e.clientX);
          }
        }}
      >
        <img src={beforeImage} alt="Before" className="bac-before-image" />
        <img 
          src={afterImage} 
          alt="After" 
          className="bac-after-image"
          style={{ clipPath: `inset(0 0 0 ${sliderPosition}%)` }}
        />
        <span className="bac-before-label">Before</span>
        <span className="bac-after-label">After</span>
        <div 
          className="bac-slider-handle"
          style={{ left: `${sliderPosition}%` }}
          onMouseDown={handleMouseDown}
          onTouchStart={handleMouseDown}
        />
      </div>
      <div className="bac-pair-label">Pair {pairNumber} of {totalPairs}</div>
    </div>
  );
};

export { ComparisonSlider };
export default BeforeAfterCamera;